import React, { type ChangeEvent } from "react";
import ModalForm from "@ui/Modal/Form";
import t from "../../../../../i18n";
import Text from "@ui/Text";
import { css } from "@linaria/core";
import { color } from "@ui";
import { spacings } from "@ui/Theme";
import type { FailureValueStatus } from "../../../../../api/useFailureMetadata";
import formatDate from "@ui/i18n/formatDate";
import Select from "@ui/Select";
import { getFaultStatusText } from "../../../Fault";
import { useSettings } from "../../../../../api/useSettings";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import type { FaultEvent } from "../../../../../api/useFaultEvents";
import { getEventStatusText } from "../../utils/faultEventStatus";
import Checkbox from "@ui/Checkbox";
import type { Project } from "../../../../../api/useProjects";
import copyDeep from "lodash.clonedeep";
import type { Period } from "@ui/Highcharts/hooks/usePeriod";

interface P {
  project: Project;
  event: FaultEvent;
  defaultFaultStatus: FailureValueStatus;
  period: Period;
  analysesGroupedByFaultStatus: {
    inZoom: {
      [faultStatus in FailureValueStatus]: { date: number }[];
    };
    offZoom: {
      [faultStatus in FailureValueStatus]: { date: number }[];
    };
  };
  totalAnalysesCount: number;
  handleOnSubmit: ({
    currentFaultStatus,
    newFaultStatus,
    applyToNewDetection,
  }: {
    currentFaultStatus: FailureValueStatus;
    newFaultStatus: FailureValueStatus;
    applyToNewDetection: boolean;
  }) => Promise<boolean>;
  closeModal: () => void;
}

const ModalFormBatchFaultStatusChange = ({
  project,
  event,
  defaultFaultStatus,
  period,
  analysesGroupedByFaultStatus,
  totalAnalysesCount,
  handleOnSubmit,
  closeModal,
}: P) => {
  const settings = useSettings();

  const [newFaultStatus, setNewFaultStatus] = React.useState(defaultFaultStatus);
  const [shouldRelaunchDiagnosis, setShouldRelaunchDiagnosis] = React.useState(
    settings.AUTOMATIC_DIAGNOSIS_RELAUNCH,
  );
  const [applyToNewDetection, setShouldApplyStatusToNewDetections] = React.useState<boolean>(
    event.metadata.newDiagnosesStatus ===
      convertFaultStatus(newFaultStatus, settings.AUTOMATIC_DIAGNOSIS_RELAUNCH),
  );

  const onRelaunchCheckboxChange = React.useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setShouldRelaunchDiagnosis(e.target.checked);
      setShouldApplyStatusToNewDetections(
        event.metadata.newDiagnosesStatus === convertFaultStatus(newFaultStatus, e.target.checked),
      );
    },
    [event.metadata.newDiagnosesStatus, newFaultStatus],
  );
  const onUpdateFaultStatus = React.useCallback(
    (status: FailureValueStatus) => {
      setNewFaultStatus(status);
      if (event.metadata.newDiagnosesStatus === convertFaultStatus(status, shouldRelaunchDiagnosis))
        setShouldApplyStatusToNewDetections(true);
      else setShouldApplyStatusToNewDetections(false);
    },
    [setNewFaultStatus, event.metadata.newDiagnosesStatus, shouldRelaunchDiagnosis],
  );

  const dirty = React.useMemo(
    () => defaultFaultStatus !== convertFaultStatus(newFaultStatus, shouldRelaunchDiagnosis),
    [defaultFaultStatus, newFaultStatus, shouldRelaunchDiagnosis],
  );

  const options = React.useMemo(() => faultStatusOptions(defaultFaultStatus), [defaultFaultStatus]);

  const impactedAnalysesByStatusChange = React.useMemo(
    () => analysesGroupedByFaultStatus["inZoom"][defaultFaultStatus] ?? [],
    [analysesGroupedByFaultStatus, defaultFaultStatus],
  );

  const areAllDetectionsFalse = checkIfAllDetectionsAreFalse(
    analysesGroupedByFaultStatus,
    defaultFaultStatus,
    newFaultStatus as FailureValueStatus,
    totalAnalysesCount,
  );
  const isTransitionToInProgressFaultEventStatus =
    event.metadata.status === "TO_BE_ANALYSED" && !areAllDetectionsFalse;
  const isTransitionToClosedFaultEventStatus = areAllDetectionsFalse;

  const onSubmit = React.useCallback(async () => {
    await handleOnSubmit({
      currentFaultStatus: defaultFaultStatus,
      newFaultStatus: convertFaultStatus(newFaultStatus, shouldRelaunchDiagnosis),
      applyToNewDetection,
    });
    return true;
  }, [
    newFaultStatus,
    defaultFaultStatus,
    shouldRelaunchDiagnosis,
    applyToNewDetection,
    handleOnSubmit,
  ]);

  const startPeriodString = formatDate(new Date(period.from), "slash", project.timezone);
  const endPeriodString = formatDate(new Date(period.to), "slash", project.timezone);

  const isDisableCheckboxApplyToNewDetection =
    event.metadata.newDiagnosesStatus ===
      convertFaultStatus(newFaultStatus, shouldRelaunchDiagnosis) ||
    convertFaultStatus(newFaultStatus, shouldRelaunchDiagnosis) === defaultFaultStatus;

  return (
    <ModalForm
      onRequestClose={closeModal}
      onSubmit={dirty ? onSubmit : false}
      okButtonName={t(
        "project.fault_event.evolution_chart.batch_status_change.modal.form.change_status.button.name",
      )}
      cancellable
    >
      <div>
        <Text>
          {t("project.fault_event.batch_status_change.modal.form.change_status.impacted_period")}{" "}
          <Text className={carbon800} type="body1SemiBold" as="span">
            {startPeriodString} - {endPeriodString}
          </Text>
        </Text>
        <Text className={marginTopXs}>
          {t(
            "project.fault_event.batch_status_change.modal.form.change_status.nb_impacted_detections",
          )}{" "}
          <Text className={carbon800} type="body1SemiBold" as="span">
            {impactedAnalysesByStatusChange.length}/{totalAnalysesCount}
          </Text>
        </Text>
      </div>
      <Select
        label={t("project.fault_event.modal_form.select.status.label")}
        prompt=""
        defaultValue={defaultFaultStatus}
        value={newFaultStatus}
        onChange={(e: ChangeEvent<HTMLSelectElement>) => {
          onUpdateFaultStatus(e.target.value as FailureValueStatus);
        }}
        options={options}
        clearable={false}
        revertable={dirty && !isFaultDetection(newFaultStatus)}
        revert={() => onUpdateFaultStatus(defaultFaultStatus)}
      />
      {dirty || isFaultDetection(newFaultStatus) ? (
        <>
          {isFaultDetection(newFaultStatus) ? (
            <Checkbox checked={shouldRelaunchDiagnosis} onChange={onRelaunchCheckboxChange}>
              {t(
                "project.fault_event.evolution_chart.batch_status_change.modal.form.change_status.relunch_diagnosis",
              )}
            </Checkbox>
          ) : null}
          <div
            title={
              event.metadata.newDiagnosesStatus ===
              convertFaultStatus(newFaultStatus, shouldRelaunchDiagnosis)
                ? t(
                    "project.fault_event.evolution_chart.batch_status_change.modal.form.change_status.tooltip.apply_to_new_detections.already_set",
                  )
                : undefined
            }
          >
            <Checkbox
              title={
                isDisableCheckboxApplyToNewDetection
                  ? t(
                      "project.fault_event.evolution_chart.batch_status_change.modal.form.change_status.checkbox.apply_to_new_detections.disabled",
                    )
                  : undefined
              }
              checked={applyToNewDetection}
              onChange={() => setShouldApplyStatusToNewDetections(s => !s)}
              disabled={isDisableCheckboxApplyToNewDetection}
            >
              {t(
                "project.fault_event.evolution_chart.batch_status_change.modal.form.change_status.checkbox.apply_to_new_detections",
              )}
            </Checkbox>
          </div>
          {isTransitionToInProgressFaultEventStatus ? (
            <AdditionnalMessage
              status="INFO"
              text={t(
                "project.fault_event.evolution_chart.batch_status_change.modal.form.additionnal_message.update_fault_status",
                getEventStatusText("ONGOING"),
              )}
            />
          ) : null}
          {isTransitionToClosedFaultEventStatus ? (
            <AdditionnalMessage
              status="INFO"
              text={t(
                "project.fault_event.evolution_chart.batch_status_change.modal.form.additionnal_message.update_fault_status_and_closed_event",
              )}
            />
          ) : null}
        </>
      ) : null}
    </ModalForm>
  );
};

export default ModalFormBatchFaultStatusChange;

// #region CSS
const carbon800 = css`
  color: ${color.carbon800};
`;
const marginTopXs = css`
  margin-top: ${spacings.xs};
`;
// #endregion CSS

// #region Functions
function convertFaultStatus(
  status: FailureValueStatus,
  shouldRelaunchDiagnosis: boolean,
): FailureValueStatus {
  if (isFaultDetection(status)) {
    if (shouldRelaunchDiagnosis) {
      return "REJECTED";
    } else {
      return "LEGACY_REJECTED";
    }
  }
  return status;
}
function faultStatusOptions(status: FailureValueStatus) {
  /**
   * By default, the false detection option is set to LEGACY_REJECTED. The choice between REJECTED and LEGACY_REJECTED
   * for false detection should be handled at the time of submission.
   */
  switch (status) {
    case "TO_BE_ANALYSED":
    case "BACK_TO_BE_ANALYSED":
      return [
        {
          value: "TO_BE_ANALYSED",
          label: getFaultStatusText("TO_BE_ANALYSED"),
        },
        {
          value: "SUSPECTED",
          label: getFaultStatusText("SUSPECTED"),
        },
        {
          value: "CONFIRMED",
          label: getFaultStatusText("CONFIRMED"),
        },
        {
          value: "LEGACY_REJECTED",
          label: getFaultStatusText("LEGACY_REJECTED"),
        },
      ];
    case "SUSPECTED":
      return [
        {
          value: "SUSPECTED",
          label: getFaultStatusText("SUSPECTED"),
        },
        {
          value: "CONFIRMED",
          label: getFaultStatusText("CONFIRMED"),
        },
        {
          value: "LEGACY_REJECTED",
          label: getFaultStatusText("LEGACY_REJECTED"),
        },
      ];
    case "CONFIRMED":
      return [
        {
          value: "SUSPECTED",
          label: getFaultStatusText("SUSPECTED"),
        },
        {
          value: "CONFIRMED",
          label: getFaultStatusText("CONFIRMED"),
        },
        {
          value: "LEGACY_REJECTED",
          label: getFaultStatusText("LEGACY_REJECTED"),
        },
      ];
    case "REJECTED":
      return [
        {
          value: "REJECTED",
          label: getFaultStatusText("REJECTED"),
        },
      ];
    case "LEGACY_REJECTED":
      return [
        {
          value: "LEGACY_REJECTED",
          label: getFaultStatusText("LEGACY_REJECTED"),
        },
        {
          value: "SUSPECTED",
          label: getFaultStatusText("SUSPECTED"),
        },
      ];
    default:
      throw new Error(status satisfies never);
  }
}

function concatZoomData(data: {
  inZoom: {
    [faultStatus in FailureValueStatus]: { date: number }[];
  };
  offZoom: {
    [faultStatus in FailureValueStatus]: { date: number }[];
  };
}) {
  const result: { [faultStatus in FailureValueStatus]: { date: number }[] } = {
    SUSPECTED: [],
    CONFIRMED: [],
    REJECTED: [],
    LEGACY_REJECTED: [],
    TO_BE_ANALYSED: [],
    BACK_TO_BE_ANALYSED: [],
  };

  for (const status of Object.keys(result) as FailureValueStatus[]) {
    result[status] = [...data.inZoom[status], ...data.offZoom[status]];
  }

  return result;
}
export function checkIfAllDetectionsAreFalse(
  analysesGroupedByFaultStatus: {
    inZoom: {
      [faultStatus in FailureValueStatus]: { date: number }[];
    };
    offZoom: {
      [faultStatus in FailureValueStatus]: { date: number }[];
    };
  },
  faulStatus: FailureValueStatus,
  newFaultStatus: FailureValueStatus,
  totalAnalysesCount: number,
): boolean {
  const inZoom = copyDeep(analysesGroupedByFaultStatus["inZoom"]);
  inZoom[newFaultStatus].push(...inZoom[faulStatus]);
  inZoom[faulStatus] = [];

  const concat = concatZoomData({ inZoom, offZoom: analysesGroupedByFaultStatus.offZoom });

  return concat["REJECTED"].length + concat["LEGACY_REJECTED"].length === totalAnalysesCount;
}
function isFaultDetection(newFaultStatus: FailureValueStatus): boolean {
  return ["REJECTED", "LEGACY_REJECTED"].includes(newFaultStatus);
}
// #endregion Functions
